import { useMemo, useState } from 'react';
import styled from 'styled-components';

import useInvalidatedPatientQueries from 'components/patient/useInvalidatedPatientQueries';
import ActivityInput from 'components/shared/activityInput/ActivityInput';
import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import useMutateWithToast from 'hooks/useMutateWithToast';
import Group from 'models/Group';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { updateReview } from 'services/api/reviews';
import colors from 'styles/theme/colors';

type AcceptReferralModalProps = {
  handleClose: () => void;
  patientName: string;
  referredBy: Group;
  locationEpisode: LocationEpisode;
  review: Referral;
};

const AcceptReferralModal = ({ handleClose, patientName, locationEpisode, review }: AcceptReferralModalProps) => {
  const referredBy = useMemo(() => locationEpisode.group, [locationEpisode]);

  const { profile } = useProfile();
  const invalidatePatientQueries = useInvalidatedPatientQueries();

  const { note, setNote } = useActivityInput();
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: () => {
      invalidatePatientQueries({
        locationEpisode: { id: locationEpisode.id },
        referrals: true,
        referral: { id: review.id },
        attachmentsCount: true,
      });

      handleClose();
    },
    onSettled: () => {
      setAttachmentUploading(false);
    },
  });

  const handleSubmit = () => {
    delete note?.escalationType;

    mutate({
      id: review.id,
      status: ReviewStatus.ACCEPTED,
      reviewedById: profile?.id,
      note: note
        ? {
            ...note,
            attachments: note.attachments?.map((upload) => upload.serialize()),
          }
        : undefined,
    });
  };

  return (
    <StandardModal title='Accept Referral' onCancel={handleClose}>
      <StandardModal.PatientName>Patient: {patientName}</StandardModal.PatientName>
      <StandardModal.SubHeader>
        Accepting this referral will notify{' '}
        <b>
          {referredBy.name} ({referredBy.groupType.displayName})
        </b>{' '}
        and be reflected on the patient story.
      </StandardModal.SubHeader>
      <InputContainer>
        <label>Provide any additional notes for {referredBy.name}</label>
        <ActivityInput
          locationEpisodeId={locationEpisode.id}
          values={note}
          setValues={setNote}
          onUploading={setAttachmentUploading}
        />
      </InputContainer>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='primary' disabled={attachmentUploading || isPending} onClick={handleSubmit}>
          Accept Referral
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
};

export default AcceptReferralModal;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 36px;

  label {
    color: ${colors.black75};
    font-size: 12px;
    margin-bottom: 4px;
  }
`;
